import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Separator } from '../components/ui/separator';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Coins, Plus, Minus, ShoppingCart, History, Bell, AlertTriangle, TrendingDown, Loader2, Filter, Download, ChevronLeft, ChevronRight, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext.js';

export default function PPDCreditsCard() {
  const { api } = useAuth();
  const [balance, setBalance] = useState(0);
  const [thresholds, setThresholds] = useState([]);
  const [tiers, setTiers] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [txTotal, setTxTotal] = useState(0);
  const [txPage, setTxPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [purchasingTier, setPurchasingTier] = useState(null);
  const [newThreshold, setNewThreshold] = useState('');
  const [savingThresholds, setSavingThresholds] = useState(false);

  // Ledger modal state
  const [ledgerOpen, setLedgerOpen] = useState(false);
  const [ledgerItems, setLedgerItems] = useState([]);
  const [ledgerTotal, setLedgerTotal] = useState(0);
  const [ledgerPage, setLedgerPage] = useState(1);
  const [ledgerType, setLedgerType] = useState('all');
  const [ledgerDateFrom, setLedgerDateFrom] = useState('');
  const [ledgerDateTo, setLedgerDateTo] = useState('');
  const [ledgerLoading, setLedgerLoading] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [balRes, tierRes, txRes] = await Promise.all([
        api.get('/ppd/balance'),
        api.get('/ppd/tiers'),
        api.get(`/ppd/transactions?page=${txPage}&page_size=10`),
      ]);
      setBalance(balRes.data.balance);
      setThresholds(balRes.data.thresholds || []);
      setTiers(tierRes.data || []);
      setTransactions(txRes.data.items || []);
      setTxTotal(txRes.data.total || 0);
    } catch (e) {
      console.error('PPD fetch error:', e);
    } finally {
      setLoading(false);
    }
  }, [txPage]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Ledger fetcher
  const fetchLedger = useCallback(async () => {
    setLedgerLoading(true);
    try {
      const params = new URLSearchParams({ page: ledgerPage, page_size: 25 });
      if (ledgerType !== 'all') params.set('type', ledgerType);
      if (ledgerDateFrom) params.set('date_from', ledgerDateFrom);
      if (ledgerDateTo) params.set('date_to', ledgerDateTo);
      const res = await api.get(`/ppd/transactions?${params}`);
      setLedgerItems(res.data.items || []);
      setLedgerTotal(res.data.total || 0);
    } catch (e) {
      console.error('Ledger fetch error:', e);
    } finally {
      setLedgerLoading(false);
    }
  }, [api, ledgerPage, ledgerType, ledgerDateFrom, ledgerDateTo]);

  useEffect(() => {
    if (ledgerOpen) fetchLedger();
  }, [ledgerOpen, fetchLedger]);

  const handlePurchase = async (tier) => {
    setPurchasingTier(tier.id);
    try {
      const res = await api.post(`/ppd/purchase?tier_id=${tier.id}`);
      if (res.data.checkout_url) {
        window.location.href = res.data.checkout_url;
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to start purchase');
      setPurchasingTier(null);
    }
  };

  const handleAddThreshold = () => {
    const val = parseInt(newThreshold, 10);
    if (!val || val <= 0) return;
    if (thresholds.includes(val)) { toast.error('Threshold already exists'); return; }
    setThresholds(prev => [...prev, val].sort((a, b) => b - a));
    setNewThreshold('');
  };

  const handleRemoveThreshold = (val) => {
    setThresholds(prev => prev.filter(t => t !== val));
  };

  const handleSaveThresholds = async () => {
    setSavingThresholds(true);
    try {
      const res = await api.put('/ppd/thresholds', { thresholds });
      setThresholds(res.data.thresholds);
      toast.success('Notification thresholds saved');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save thresholds');
    } finally {
      setSavingThresholds(false);
    }
  };

  const formatDate = (iso) => {
    if (!iso) return '—';
    return new Date(iso).toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  if (loading) {
    return (
      <Card data-testid="ppd-credits-card">
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card data-testid="ppd-credits-card">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-lg flex items-center gap-2">
              <Coins className="h-5 w-5 text-[#ffd108]" />
              Pay Per Docket Credits
            </CardTitle>
            <CardDescription>Pre-paid credits for PPD-licenced workers. One credit per docket submitted.</CardDescription>
          </div>
          <div className="text-right">
            <div className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Balance</div>
            <Badge
              data-testid="ppd-balance-badge"
              className={`text-lg px-3 py-1 ${balance > 0 ? 'bg-green-500 hover:bg-green-600' : balance === 0 ? 'bg-zinc-500 hover:bg-zinc-600' : 'bg-red-500 hover:bg-red-600'} text-white`}
            >
              {balance < 0 ? `−${Math.abs(balance)} (deficit)` : balance}
            </Badge>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {balance < 0 && (
          <div className="flex items-center gap-2 p-3 rounded-md bg-red-500/10 border border-red-500/20 text-red-400 text-sm" data-testid="ppd-deficit-warning">
            <AlertTriangle className="h-4 w-4 shrink-0" />
            Your balance is in deficit. New PPD worker allocations are blocked until you purchase more credits.
          </div>
        )}

        {/* Credit Pack Tiers */}
        {tiers.length > 0 && (
          <div>
            <h4 className="text-sm font-medium mb-3 flex items-center gap-2">
              <ShoppingCart className="h-4 w-4" /> Purchase Credits
            </h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {tiers.map((tier) => (
                <div key={tier.id} className="border rounded-lg p-4 flex flex-col items-center gap-2 bg-muted/30" data-testid={`ppd-tier-${tier.id}`}>
                  <div className="text-2xl font-bold">{tier.credits}</div>
                  <div className="text-xs text-muted-foreground">credits</div>
                  <div className="text-lg font-semibold">${(tier.price_cents / 100).toFixed(2)} <span className="text-xs text-muted-foreground">{tier.currency}</span></div>
                  <div className="text-[10px] text-muted-foreground">${(tier.price_cents / 100 / tier.credits).toFixed(2)} per credit</div>
                  <Button
                    size="sm"
                    className="w-full mt-1 btn-volt"
                    onClick={() => handlePurchase(tier)}
                    disabled={purchasingTier === tier.id}
                    data-testid={`ppd-purchase-${tier.id}`}
                  >
                    {purchasingTier === tier.id ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Purchase'}
                  </Button>
                </div>
              ))}
            </div>
          </div>
        )}

        {tiers.length === 0 && (
          <div className="text-sm text-muted-foreground text-center py-4 border rounded-md border-dashed">
            No credit packs available. Contact support to get started with Pay Per Docket.
          </div>
        )}

        <Separator />

        {/* Notification Thresholds */}
        <div>
          <h4 className="text-sm font-medium mb-3 flex items-center gap-2">
            <Bell className="h-4 w-4" /> Notification Thresholds
          </h4>
          <p className="text-xs text-muted-foreground mb-3">Get notified when your credit balance drops to these levels.</p>
          <div className="flex items-center gap-2 mb-3">
            <Input
              type="number"
              min="1"
              value={newThreshold}
              onChange={(e) => setNewThreshold(e.target.value)}
              placeholder="e.g. 50"
              className="w-24 h-8"
              data-testid="ppd-threshold-input"
              onKeyDown={(e) => e.key === 'Enter' && handleAddThreshold()}
            />
            <Button size="sm" variant="outline" onClick={handleAddThreshold} className="h-8" data-testid="ppd-threshold-add">
              <Plus className="h-3 w-3 mr-1" /> Add
            </Button>
          </div>
          {thresholds.length > 0 ? (
            <div className="flex flex-wrap gap-2 mb-3">
              {thresholds.map((t) => (
                <Badge key={t} variant="secondary" className="text-sm px-2 py-1 gap-1">
                  {t} credits
                  <button onClick={() => handleRemoveThreshold(t)} className="ml-1 hover:text-destructive" data-testid={`ppd-threshold-remove-${t}`}>
                    <Minus className="h-3 w-3" />
                  </button>
                </Badge>
              ))}
            </div>
          ) : (
            <p className="text-xs text-muted-foreground mb-3">No thresholds set.</p>
          )}
          <Button size="sm" variant="outline" onClick={handleSaveThresholds} disabled={savingThresholds} data-testid="ppd-save-thresholds">
            {savingThresholds ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
            Save Thresholds
          </Button>
        </div>

        <Separator />

        {/* Transaction History (preview) */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-sm font-medium flex items-center gap-2">
              <History className="h-4 w-4" /> Recent Transactions
            </h4>
            <Button size="sm" variant="outline" className="h-7 text-xs gap-1" onClick={() => { setLedgerPage(1); setLedgerOpen(true); }} data-testid="ppd-open-ledger">
              <ExternalLink className="h-3 w-3" /> Full Ledger
            </Button>
          </div>
          {transactions.length > 0 ? (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-xs">Date</TableHead>
                    <TableHead className="text-xs">Type</TableHead>
                    <TableHead className="text-xs">Reference</TableHead>
                    <TableHead className="text-xs text-right">Credits</TableHead>
                    <TableHead className="text-xs text-right">Balance</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {transactions.slice(0, 5).map((tx) => (
                    <TableRow key={tx.id}>
                      <TableCell className="text-xs">{formatDate(tx.created_at)}</TableCell>
                      <TableCell>
                        <Badge variant="secondary" className={`text-[10px] ${tx.type === 'purchase' ? 'bg-green-500/20 text-green-400' : 'bg-orange-500/20 text-orange-400'}`}>
                          {tx.type === 'purchase' ? 'Purchase' : 'Used'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-xs max-w-[200px] truncate">{tx.reference}</TableCell>
                      <TableCell className={`text-xs text-right font-medium ${tx.credits > 0 ? 'text-green-500' : 'text-red-400'}`}>
                        {tx.credits > 0 ? `+${tx.credits}` : tx.credits}
                      </TableCell>
                      <TableCell className="text-xs text-right">{tx.balance_after}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {txTotal > 5 && (
                <p className="text-xs text-muted-foreground text-center mt-2">
                  Showing 5 of {txTotal} transactions.{' '}
                  <button className="text-[#ffd108] underline" onClick={() => { setLedgerPage(1); setLedgerOpen(true); }}>View all</button>
                </p>
              )}
            </>
          ) : (
            <p className="text-xs text-muted-foreground text-center py-4">No transactions yet.</p>
          )}
        </div>

        {/* Full Ledger Dialog */}
        <Dialog open={ledgerOpen} onOpenChange={setLedgerOpen}>
          <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="ppd-ledger-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <History className="h-5 w-5" /> PPD Credit Ledger
              </DialogTitle>
            </DialogHeader>
            {/* Filters */}
            <div className="flex flex-wrap gap-3 items-end mb-4">
              <div className="space-y-1">
                <Label className="text-xs">Type</Label>
                <Select value={ledgerType} onValueChange={(v) => { setLedgerType(v); setLedgerPage(1); }}>
                  <SelectTrigger className="w-[130px] h-8 text-xs" data-testid="ppd-ledger-type-filter">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="purchase">Purchases</SelectItem>
                    <SelectItem value="consumption">Usage</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">From</Label>
                <Input type="date" className="w-[140px] h-8 text-xs" value={ledgerDateFrom} onChange={(e) => { setLedgerDateFrom(e.target.value); setLedgerPage(1); }} data-testid="ppd-ledger-date-from" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">To</Label>
                <Input type="date" className="w-[140px] h-8 text-xs" value={ledgerDateTo} onChange={(e) => { setLedgerDateTo(e.target.value); setLedgerPage(1); }} data-testid="ppd-ledger-date-to" />
              </div>
              {(ledgerType !== 'all' || ledgerDateFrom || ledgerDateTo) && (
                <Button variant="ghost" size="sm" className="h-8 text-xs" onClick={() => { setLedgerType('all'); setLedgerDateFrom(''); setLedgerDateTo(''); setLedgerPage(1); }}>
                  Clear filters
                </Button>
              )}
            </div>

            {ledgerLoading ? (
              <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
            ) : ledgerItems.length > 0 ? (
              <>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="text-xs">Date</TableHead>
                      <TableHead className="text-xs">Type</TableHead>
                      <TableHead className="text-xs">Reference</TableHead>
                      <TableHead className="text-xs">Operator</TableHead>
                      <TableHead className="text-xs text-right">Credits</TableHead>
                      <TableHead className="text-xs text-right">Balance After</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {ledgerItems.map((tx) => (
                      <TableRow key={tx.id} data-testid={`ppd-ledger-row-${tx.id}`}>
                        <TableCell className="text-xs whitespace-nowrap">{formatDate(tx.created_at)}</TableCell>
                        <TableCell>
                          <Badge variant="secondary" className={`text-[10px] ${tx.type === 'purchase' ? 'bg-green-500/20 text-green-400' : 'bg-orange-500/20 text-orange-400'}`}>
                            {tx.type === 'purchase' ? 'Purchase' : 'Used'}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-xs max-w-[200px] truncate">{tx.reference}</TableCell>
                        <TableCell className="text-xs">{tx.operator_name || '—'}</TableCell>
                        <TableCell className={`text-xs text-right font-medium ${tx.credits > 0 ? 'text-green-500' : 'text-red-400'}`}>
                          {tx.credits > 0 ? `+${tx.credits}` : tx.credits}
                        </TableCell>
                        <TableCell className="text-xs text-right font-mono">{tx.balance_after}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                <div className="flex items-center justify-between mt-4">
                  <span className="text-xs text-muted-foreground">{ledgerTotal} total transactions</span>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" disabled={ledgerPage <= 1} onClick={() => setLedgerPage(p => p - 1)} data-testid="ppd-ledger-prev">
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <span className="text-xs text-muted-foreground self-center">Page {ledgerPage} of {Math.ceil(ledgerTotal / 25) || 1}</span>
                    <Button size="sm" variant="outline" disabled={ledgerPage >= Math.ceil(ledgerTotal / 25)} onClick={() => setLedgerPage(p => p + 1)} data-testid="ppd-ledger-next">
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </>
            ) : (
              <p className="text-sm text-muted-foreground text-center py-8">No transactions match your filters.</p>
            )}
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
}
